import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validator,
} from '@angular/forms';
import { isNullOrUndefined } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { minMaxRangeValidator } from 'src/app/form-config/validators/cross-field-validators/min-max-range.validator';
import { valueWithinRangeValidator } from 'src/app/form-config/validators/cross-field-validators/value-within-range.validator';
import { DuplicateMinusOrComma } from 'src/app/product-capture/utils/duplicate-minus-or-comma';
import { LeadingZeros } from 'src/app/product-capture/utils/leading-zeros';
import { DcControlValidationComponent } from '../dc-control-validation/dc-control-validation.component';
import { DcInputNumberPercentIndicativeFinalService } from './dc-input-number-percent-indicative-final.service';

interface ValueGroup {
  value: string | null;
  indicativeValue: string | null;
  minValue: string | null;
  maxValue: string | null;
}

@Component({
  selector: 'app-dc-input-number-percent-indicative-final',
  templateUrl: './dc-input-number-percent-indicative-final.component.html',
  styleUrls: ['./dc-input-number-percent-indicative-final.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DcInputNumberPercentIndicativeFinalComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DcInputNumberPercentIndicativeFinalComponent,
      multi: true,
    },
  ],
})
export class DcInputNumberPercentIndicativeFinalComponent
  implements ControlValueAccessor, OnDestroy, Validator, OnChanges
{
  @Input() showPlaceholder = true;
  @Input() label: string;
  @Input() id: string;
  @Input() helpText?: string;
  @Input() field: any;
  @Input() defaultUnit: 'abs' | 'pct' = 'pct';
  @Input() lockDefault: boolean = false;
  @Input() defaultMode: 'final' | 'indicative' = 'final';
  @Input() readonly: boolean = false;
  @Input() decimalPlaces: number;
  @Input() minMaxIndicativeToggle: boolean = false; // Allows you to make it a togglable range-selector if true
  @Input() showUnitToggle: boolean = true;
  @Input() componentKey: string; // Assign a random GUID to your component and any changes will be fed to
  // the DcInputNumberPercentIndicativeFinalService. This is used to track whether we are using the 'range'
  // fields anywhere, and if true adds a Product Finalisation Date to the Key Dates modal. If the component
  // instance has minMaxIndicativeToggle set to true, you should also add a componentKey

  @Input()
  customErrorMessage: string;

  control: AbstractControl;

  @HostListener('input', ['$event.target.value'])
  onChange = (_: any) => {};
  onChangeSubs: Subscription[] = [];

  onTouched = () => {};

  unit: 'abs' | 'pct';
  finalOrIndicative: 'final' | 'indicative';

  form: UntypedFormGroup = this.fb.group(
    {
      value: [null],
      minValue: [null],
      indicativeValue: [null],
      maxValue: [null],
    },
    {
      validators: [
        minMaxRangeValidator('minValue', 'maxValue'),
        valueWithinRangeValidator('minValue', 'maxValue', 'indicativeValue'),
        valueWithinRangeValidator('minValue', 'maxValue', 'value'),
      ],
    }
  );

  @ViewChildren(DcControlValidationComponent)
  activeControlValidation: QueryList<DcControlValidationComponent>;

  constructor(
    private fb: UntypedFormBuilder,
    private dcInputNumberPercentIndicativeFinalService: DcInputNumberPercentIndicativeFinalService
  ) {}

  ngOnInit(): void {
    const subscription = this.form.valueChanges.subscribe((values) => {
      if (this.componentKey) {
        this.dcInputNumberPercentIndicativeFinalService.updateValues(
          this.componentKey,
          this.rangeSelectionIdentifier(values)
        );
      }

      this.form.touched && this.writeValue(values);

      const modifiedValues = {
        value: this.percentAppender(values.value),
        indicativeValue: this.percentAppender(values.indicativeValue),
        minValue: this.percentAppender(values.minValue),
        maxValue: this.percentAppender(values.maxValue),
      };

      setTimeout(() => this.onChange?.(modifiedValues));
    });
    this.onChangeSubs.push(subscription);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly) {
      if (this.readonly) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }

    if (
      'defaultUnit' in changes &&
      (!this.unit || this.allValuesAreNullOrEmpty(this.form.value))
    ) {
      this.setUnit(changes.defaultUnit.currentValue);
    }
  }

  setUnit(type: 'abs' | 'pct'): void {
    this.unit = type;
  }

  percentAppender(value: string | null): string | null {
    if (value) {
      return this.unit === 'pct' && !value.endsWith('%') ? value + '%' : value;
    }
    return null;
  }

  rangeSelectionIdentifier(values: ValueGroup | null): boolean {
    return !!(values?.minValue || values?.indicativeValue || values?.maxValue);
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  writeValue(values: {
    value: string | null;
    indicativeValue: string | null;
    minValue: string | null;
    maxValue: string | null;
  }): void {
    if (!values) {
      this.form.reset(
        {
          value: null,
          minValue: null,
          indicativeValue: null,
          maxValue: null,
        },
        { emitEvent: false }
      );
      this.unit = this.defaultUnit;
      this.finalOrIndicative = this.defaultMode;
      return;
    }

    // Due to some form config effects, sometimes the `writeValue` function is
    // called with `values` being a string initially, and subsequently with
    // the correct object. Hence, it is safe to ignore the string values.
    if (typeof values === 'string') {
      return;
    }

    if (
      values.value !== this.form.get('value').value ||
      values.minValue !== this.form.get('minValue').value ||
      values.indicativeValue !== this.form.get('indicativeValue').value ||
      values.maxValue !== this.form.get('maxValue').value ||
      (!values.value &&
        !values.minValue &&
        !values.indicativeValue &&
        !values.maxValue)
    ) {
      this.form.patchValue(values, { emitEvent: false });
      const { touched } = this.form;
      this.unit = this.allValuesAreNullOrEmpty(values)
        ? !touched
          ? this.defaultUnit
          : this.unit
        : this.determineUnitType(values);
      this.finalOrIndicative = this.determineMode(values);
    }
  }

  determineUnitType(obj: ValueGroup): 'abs' | 'pct' {
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== null) {
        if (typeof obj[key] === 'string' && obj[key].endsWith('%')) {
          return 'pct';
        }
      }
    }
    return this.lockDefault ? this.defaultUnit ?? 'abs' : 'abs';
  }

  determineMode(obj: ValueGroup): 'final' | 'indicative' {
    const { value, indicativeValue, minValue, maxValue } = obj;

    if (!value && (indicativeValue || minValue || maxValue)) {
      return 'indicative';
    }
    return 'final';
  }

  allValuesAreNullOrEmpty(obj: ValueGroup): boolean {
    for (const key in obj) {
      const value = obj[key];
      if (typeof value === 'string') {
        if (!value?.trim().length) {
          continue;
        }
      }
      if (!isNullOrUndefined(obj[key])) {
        return false;
      }
    }
    return true;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  toggleValues(): void {
    this.unit = this.unit === 'pct' ? 'abs' : 'pct';
    this.convertValues();
    this.onChange?.(this.form.value);
  }

  convertValues(): void {
    this.form.patchValue({
      value: this.percentNumberConversion(
        this.form.get('value').value,
        this.unit
      ),
      indicativeValue: this.percentNumberConversion(
        this.form.get('indicativeValue').value,
        this.unit
      ),
      minValue: this.percentNumberConversion(
        this.form.get('minValue').value,
        this.unit
      ),
      maxValue: this.percentNumberConversion(
        this.form.get('maxValue').value,
        this.unit
      ),
    });
  }

  percentNumberConversion(
    value: string | null,
    unit: 'abs' | 'pct'
  ): string | null {
    if (value) {
      const valueAsNumber = parseFloat(value);
      const convertedValue =
        unit === 'pct' ? valueAsNumber * 100 : valueAsNumber / 100;
      return parseFloat(convertedValue.toFixed(4)).toString();
    }
    return null;
  }

  toggleFinal(): void {
    this.finalOrIndicative === 'final'
      ? (this.finalOrIndicative = 'indicative')
      : (this.finalOrIndicative = 'final');
  }

  validate(control: AbstractControl) {
    this.control = control;
    if (this.form.valid) {
      this.control.setErrors(null);
      return null;
    }

    let errors: any = control.errors || {};

    errors = this.addControlErrors(errors, 'value');
    errors = this.addControlErrors(errors, 'minValue');
    errors = this.addControlErrors(errors, 'indicativeValue');
    errors = this.addControlErrors(errors, 'maxValue');

    return errors;
  }

  addControlErrors(allErrors: any, controlName: string) {
    const errors = { ...allErrors };

    const controlErrors = this.form.controls[controlName].errors;

    if (controlErrors) {
      errors[controlName] = controlErrors;
    }

    return errors;
  }

  onInputChange(newValue: string, formControlName) {
    let formattedValue = newValue
      .replace(/,/g, '') // removes ','
      .replace(/(?!^-)-/g, '') // removes '-' unless used once to denote negative number
      .replace(/(?<!\d)\.|(?<=\..*?)\./g, '') // removes '.' if there is no number before it, or a decimal already exists
      .replace(/k/gi, '000') // converts k or K > 000
      .replace(/m/gi, '000000'); // converts m or M > 000000

    this.form
      .get(formControlName)
      .setValue(formattedValue, { emitEvent: true });
  }

  onKeyDown(event: KeyboardEvent, unit: 'abs' | 'pct'): void {
    let allowedKeys;

    allowedKeys =
      unit === 'abs'
        ? [
            '-',
            '0',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '.',
            'k',
            'K',
            'm',
            'M',
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Tab',
          ]
        : [
            '-',
            '0',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '.',
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Tab',
          ];

    const inputElement = event.target as HTMLInputElement;
    const valuePriorToThisKeyPress = inputElement.value;

    if (
      !allowedKeys.includes(event.key) ||
      DuplicateMinusOrComma(event.key, valuePriorToThisKeyPress) ||
      LeadingZeros(event.key, valuePriorToThisKeyPress)
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  get valueControl(): AbstractControl {
    return this.form.get('value');
  }

  get minValueControl(): AbstractControl {
    return this.form.get('minValue');
  }

  get indicativeValueControl(): AbstractControl {
    return this.form.get('indicativeValue');
  }

  get maxValueControl(): AbstractControl {
    return this.form.get('maxValue');
  }

  ngOnDestroy(): void {
    for (let sub of this.onChangeSubs) {
      sub.unsubscribe();
    }
  }

  getValidationStatus(): string {
    return this.control.invalid ? 'invalid' : '';
  }
}
